import React, { useEffect, useRef, useState } from "react";
import "./ChatAnimation.css";

interface ChatAnimationProps {
  onComplete?: () => void;
  token?: number;
  loop?: boolean;
  botSpeed?: number; // 1 = normal, 2 = 2x, etc.
}

const ChatAnimation: React.FC<ChatAnimationProps> = ({
  onComplete,
  token,
  loop = false,
  botSpeed = 1,
}) => {
  const speedRef = useRef(botSpeed);
  useEffect(() => {
    speedRef.current = botSpeed;
  }, [botSpeed]);

  const chatRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const sendBtnRef = useRef<HTMLButtonElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const countRef = useRef<HTMLSpanElement>(null);

  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(isPaused);

  // Atualizar ref quando estado muda
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // ===== ciclo de vida / abort =====
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const abortRef = useRef<AbortController | null>(null);
  const makeAbort = () => { abortRef.current?.abort(); abortRef.current = new AbortController(); };
  const isAborted = () => !!abortRef.current?.signal.aborted;

  const completedRef = useRef(false);
  const completeOnce = () => {
    if (completedRef.current && !loop) return;
    completedRef.current = true;
    if (mountedRef.current && !isAborted()) {
      if (!loop) {
      window.dispatchEvent(new CustomEvent("featureSlideComplete", { detail: { id: "chat" } }));
      onComplete?.();
      } else {
        setTimeout(() => {
          if (!mountedRef.current || isAborted()) return;
          completedRef.current = false;
          setIsRunning(false);
          run();
        }, 3000);
      }
    }
  };

  // ===== utilitários =====
  const wait = (ms: number) =>
    new Promise<void>((resolve) => {
      if (isAborted()) return resolve();

      const checkPause = () => {
        if (isPausedRef.current) {
          // console.log('Animação pausada, aguardando... isPaused:', isPausedRef.current);
          setTimeout(checkPause, 3000);
        } else {
          // console.log('Animação retomada, continuando...');
          const id = setTimeout(() => { if (!isAborted()) resolve(); }, ms);
          abortRef.current?.signal.addEventListener("abort", () => { clearTimeout(id); resolve(); }, { once: true });
        }
      };

      checkPause();
    });

  const scrollBottom = () => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  };

  function ensureRowTopVisible(row: HTMLElement | null) {
    if (!row || !chatRef.current) return;
    const top = row.offsetTop - 12;
    chatRef.current.scrollTo({ top, behavior: "instant" as ScrollBehavior });
  }

  const icon = (p: string) =>
    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">${p}</svg>`;
  const TB = {
    copy: icon('<rect x="9" y="9" width="10" height="10" rx="2"/><rect x="5" y="5" width="10" height="10" rx="2"/>'),
    edit: icon('<path d="M4 21h4l11-11-4-4L4 17v4z"/>'),
    open: icon('<path d="M14 3h7v7"/><path d="M21 3l-9 9"/><rect x="3" y="7" width="11" height="14" rx="2"/>'),
    refresh: icon('<path d="M4.5 4.5v6h6"/><path d="M19.5 19.5v-6h-6"/><path d="M20 9A8 8 0 0 0 6 6M18 18a8 8 0 0 1-14-3"/>'),
  };
  const CHART = icon('<path d="M3 3v18h18"/><path d="M7 15v-5"/><path d="M12 15V7"/><path d="M17 15v-8"/>');

  const botAvatarSVG = () =>
    icon(`
    <path d="M12 3v2"/>
    <rect x="6" y="7" width="12" height="9" rx="2"/>
    <path d="M6 11H4M20 11h-2"/>
      <circle cx="10" cy="11" r="1.2"/><circle cx="14" cy="11" r="1.2"/>
    <path d="M9 16v1a3 3 0 0 0 6 0v-1"/>
  `);

  function makeMsg(role: "user" | "bot") {
    if (isAborted()) return { row: null as any, bubble: document.createElement("div") };

    const row = document.createElement("div");
    row.className = `msg ${role}`;

    const avatar = document.createElement("div");
    avatar.className = "avatar";
    if (role === "bot") avatar.innerHTML = botAvatarSVG();
    else {
      avatar.classList.add("user");
      avatar.innerHTML = icon('<circle cx="12" cy="8" r="4"/><path d="M4 20c1.5-3.5 5-5 8-5s6.5 1.5 8 5"/>');
    }

    const bubble = document.createElement("div");
    bubble.className = "bubble";

    if (role === "bot") {
      bubble.innerHTML = `<span class="label">${CHART} Insights</span>`;
      const tb = document.createElement("div");
      tb.className = "toolbar";
      tb.innerHTML = `
        <div class="tb" title="Repetir">${TB.refresh}</div>
        <div class="tb" title="Abrir">${TB.open}</div>
        <div class="tb" title="Editar">${TB.edit}</div>
        <div class="tb" title="Copiar">${TB.copy}</div>`;
      bubble.appendChild(tb);
    }

    if (role === "bot") { row.appendChild(avatar); row.appendChild(bubble); }
    else { row.appendChild(bubble); row.appendChild(avatar); }

    chatRef.current?.appendChild(row);
    scrollBottom();
    return { row, bubble };
  }

  // aplica multiplicador do bot: menor delay = mais rápido
  const scale = (ms: number) => Math.max(1, ms / (speedRef.current || 1));

  async function typeText(el: HTMLElement, text: string, base = 10) {
    if (isAborted()) return;
    el.textContent = "";
    const perChar = scale(base);
    for (const ch of text) {
      if (isAborted()) return;
      el.textContent += ch;
      scrollBottom();
      await wait(perChar);
    }
  }

  /** Componente “markdown-lite” (títulos, listas, etc.) */
  const wrapMd = (html: string) => `<div class="md">${html}</div>`;

  // ===== Streaming de HTML numa única bolha =====
  function shallowClone<T extends HTMLElement>(el: T): T {
    const clone = document.createElement(el.tagName.toLowerCase()) as T;
    clone.className = el.className;
    for (const a of Array.from(el.attributes)) if (a.name !== "class") clone.setAttribute(a.name, a.value);
    return clone;
  }
  const appendAndScroll = (parent: HTMLElement, child: Node) => { parent.appendChild(child); scrollBottom(); };

  async function showBotStreamHtml(html: string, opts: { delay?: number; headerDelay?: number } = {}) {
    const baseDelay = opts.delay ?? 35;
    const baseHeader = opts.headerDelay ?? 180;
    const charDelay = Math.min(50, scale(baseDelay));
    const headerPause = scale(baseHeader);

    if (isAborted()) return;

    const { row, bubble } = makeMsg("bot");
    ensureRowTopVisible(row);

    const content = document.createElement("div");
    content.style.marginTop = "8px";
    if (!/class\s*=\s*["']?md["']?/i.test(html)) content.className = "md";
    bubble.appendChild(content);

    const tmp = document.createElement("div");
    tmp.innerHTML = html;

    const revealChildren = async (src: Node, dst: HTMLElement) => {
      const children = Array.from(src.childNodes);
      for (const c of children) {
        await revealNode(c, dst);
        if (isAborted()) return;
      }
    };

    const revealNode = async (node: Node, host: HTMLElement) => {
      if (isAborted()) return;

      if (node.nodeType === Node.TEXT_NODE) {
        const span = document.createElement("span");
        host.appendChild(span);
        const txt = node.textContent || "";
        for (const ch of txt) {
          if (isAborted()) return;
          span.textContent += ch;
          scrollBottom();
          await wait(charDelay);
        }
        return;
      }

      if (!(node instanceof HTMLElement)) {
        appendAndScroll(host, node.cloneNode(true));
        return;
      }

      const tag = node.tagName.toLowerCase();

      if (/^h[1-6]$/.test(tag)) {
        const h = shallowClone(node);
        appendAndScroll(host, h);
        await revealChildren(node, h);
        await wait(headerPause);
        return;
      }

      if (tag === "p" || tag === "blockquote") {
        const p = shallowClone(node);
        appendAndScroll(host, p);
        await revealChildren(node, p);
        await wait(scale(100));
        return;
      }

      if (tag === "ul" || tag === "ol") {
        const list = shallowClone(node) as HTMLUListElement | HTMLOListElement;
        appendAndScroll(host, list);
        const items = Array.from(node.children) as HTMLElement[];
        for (const li of items) {
          const liClone = shallowClone(li);
          list.appendChild(liClone);
          await revealChildren(li, liClone);
          await wait(scale(80));
        }
        return;
      }

      if (tag === "table") {
        const table = shallowClone(node) as HTMLTableElement;
        table.innerHTML = "";
        appendAndScroll(host, table);

        const thead = node.querySelector("thead");
        const tbody = node.querySelector("tbody");

        if (thead) {
          table.appendChild(thead.cloneNode(true));
          await wait(scale(120));
        }
        const newTbody = document.createElement("tbody");
        table.appendChild(newTbody);

        if (tbody) {
          const rows = Array.from(tbody.querySelectorAll("tr"));
          for (const tr of rows) {
            newTbody.appendChild(tr.cloneNode(true));
            scrollBottom();
            await wait(scale(70));
          }
        }
        return;
      }

      if (tag === "hr") {
        host.appendChild(node.cloneNode(true));
        await wait(scale(80));
        return;
      }

      const holder = shallowClone(node);
      appendAndScroll(host, holder);
      await revealChildren(node, holder);
    };

    await revealChildren(tmp, content);
  }

  // ===== versão antiga (usada para casos curtos) =====
  async function showBot(textOrHtml: string, opts: { speed?: number; preHTML?: string; postEl?: HTMLElement | null; asHtml?: boolean } = {}) {
    const { speed = 8, preHTML = "", postEl = null, asHtml = false } = opts;
    if (isAborted()) return;

    const { bubble } = makeMsg("bot");
    const content = document.createElement("div");
    content.style.marginTop = "8px";
    bubble.appendChild(content);

    const typing = document.createElement("div");
    typing.className = "typing";
    typing.innerHTML = "<i></i><i></i><i></i>";
    content.appendChild(typing);
    scrollBottom();

    await wait(scale(900 + Math.min(2000, textOrHtml.length * 3)));
    if (isAborted()) return;
    typing.remove();

    if (preHTML) content.insertAdjacentHTML("afterbegin", preHTML);

    if (asHtml) {
      const span = document.createElement("div");
      span.className = "md";
      span.innerHTML = textOrHtml;
      content.appendChild(span);
    } else {
    const span = document.createElement("div");
    span.style.whiteSpace = "pre-wrap";
    content.appendChild(span);
      await typeText(span, textOrHtml, speed);
    }

    if (postEl) { content.appendChild(postEl); scrollBottom(); }
  }

  const center = (el: HTMLElement | null) => {
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  };

  const moveCursorTo = (el: HTMLElement | null, ms = 650) =>
    new Promise<void>((resolve) => {
      if (!el || !cursorRef.current || isAborted()) return resolve();
      const { x, y } = center(el);
      cursorRef.current.style.transition = `transform ${ms}ms cubic-bezier(.2,.8,.2,1)`;
      cursorRef.current.style.transform = `translate(${x - 11}px,${y - 11}px)`;
      const id = setTimeout(resolve, ms + 40);
      abortRef.current?.signal.addEventListener("abort", () => { clearTimeout(id); resolve(); }, { once: true });
    });

  async function clickEl(el: HTMLElement | null) {
    if (!el || isAborted()) return;
    el.dispatchEvent(new MouseEvent("pointerdown", { bubbles: true }));
    el.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
    (el as HTMLButtonElement).click?.();
    el.dispatchEvent(new MouseEvent("mouseup", { bubbles: true }));
    el.dispatchEvent(new MouseEvent("pointerup", { bubbles: true }));
    await wait(scale(200));
  }

  function addTime(t: string) {
    if (isAborted()) return;
    const el = document.createElement("div");
    el.className = "time";
    el.textContent = t;
    chatRef.current?.appendChild(el);
  }

  // ========== Email editor inline ==========
  function createEmailEditor() {
    const wrap = document.createElement("div");
    wrap.className = "email-editor";
    wrap.innerHTML = `
      <div class="email-editor__head">
        <span class="pill pill--warn">Pronto para revisão</span>
        <button class="btn-send" data-action="send-email">
          ${icon('<path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/>')}
          Enviar
        </button>
      </div>
      <div class="email-editor__form">
        <label>Para:</label><input value="cblock@example.com"/>
        <label>Assunto:</label><input value="Acidente com álcool — responsabilidade do seguro"/>
        <label>Mensagem:</label>
        <textarea rows="8">Olá Martim,

Só para te explicar de forma simples: mesmo que tenhas tido um acidente com álcool, o seguro é obrigado a pagar os danos ao outro carro/pessoa. No entanto, depois pode pedir-te a ti o reembolso desse valor. Ou seja, o terceiro fica protegido, mas podes ter de devolver à seguradora o que ela pagou.

Se quiseres mais detalhes ou exemplos, diz!

Abraço,
Crafteer Seguros</textarea>
      </div>
    `;
    return wrap;
  }

  const findLast = (sel: string) => {
    const all = chatRef.current?.querySelectorAll(sel);
    if (!all || all.length === 0) return null;
    return all[all.length - 1] as HTMLElement;
  };

  async function animateOpenAndSendEmail() {
    // 1) clicar no "Abrir" do cartão de email mais recente
    const openBtn = findLast('[data-action="open-email"]');
    if (!openBtn) return;

    await moveCursorTo(openBtn, 520);
    await clickEl(openBtn);

    // 2) inserir editor logo abaixo do cartão
    const card = openBtn.closest(".card") as HTMLElement | null;
    if (!card) return;

    let editor = card.nextElementSibling as HTMLElement | null;
    if (!(editor && editor.classList.contains("email-editor"))) {
      editor = createEmailEditor();
      card.insertAdjacentElement("afterend", editor);
      scrollBottom();
      await wait(300);
    }

    // 3) clicar no botão "Enviar"
    const sendBtn = editor.querySelector('[data-action="send-email"]') as HTMLElement | null;
    if (!sendBtn) return;

    await moveCursorTo(sendBtn, 550);
    await clickEl(sendBtn);

    // 4) feedback visual
    const head = editor.querySelector(".email-editor__head")!;
    const badge = document.createElement("span");
    badge.className = "pill pill--ok";
    badge.textContent = "Email enviado com sucesso";
    head.appendChild(badge);
    scrollBottom();
  }

  // ===== Fragmentos / componentes HTML =====
  const createPaymentsTable = () => {
    const wrap = document.createElement("div");
    wrap.className = "mini-table";

    const title = document.createElement("div");
    title.className = "mini-table__title";
    title.textContent = "Recebimentos (Prémios Pagos)";
    wrap.appendChild(title);

    const scroller = document.createElement("div");
    scroller.className = "mini-table__wrap";

    const table = document.createElement("table");
    table.innerHTML = `
      <thead>
        <tr>
          <th>Data</th>
          <th>Companhia</th>
          <th>Nº Apólice</th>
          <th>Ramo</th>
          <th class="num">Prémio (€)</th>
          <th class="num">Comissão (€)</th>
        </tr>
      </thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector("tbody")!;

    const payments = [
      { data: "04/12/2023", companhia: "Mapfre", apolice: "POL-430589", ramo: "Auto", premio: "1.352,72", comiss: "331,82" },
      { data: "29/12/2023", companhia: "Mapfre", apolice: "POL-430589", ramo: "Auto", premio: "287,44", comiss: "71,66" },
      { data: "27/03/2024", companhia: "Mapfre", apolice: "POL-953085", ramo: "Auto", premio: "1.641,84", comiss: "166,98" },
      { data: "01/05/2024", companhia: "Mapfre", apolice: "POL-953085", ramo: "Auto", premio: "1.654,47", comiss: "146,42" },
      { data: "11/05/2024", companhia: "Mapfre", apolice: "POL-953085", ramo: "Auto", premio: "1.814,10", comiss: "499,78" },
      { data: "27/05/2024", companhia: "Mapfre", apolice: "POL-953085", ramo: "Auto", premio: "1.238,54", comiss: "320,41" },
      { data: "30/05/2024", companhia: "Mapfre", apolice: "POL-430589", ramo: "Auto", premio: "1.755,23", comiss: "284,00" },
      { data: "03/07/2024", companhia: "Mapfre", apolice: "POL-430589", ramo: "Auto", premio: "383,80", comiss: "84,97" },
      { data: "07/07/2024", companhia: "Mapfre", apolice: "POL-953085", ramo: "Auto", premio: "1.182,72", comiss: "303,84" },
      { data: "19/07/2024", companhia: "Mapfre", apolice: "POL-430589", ramo: "Auto", premio: "1.217,08", comiss: "101,50" },
      { data: "28/07/2024", companhia: "Tranquilidade", apolice: "POL-811227", ramo: "Auto", premio: "1.813,94", comiss: "512,98" },
      { data: "05/09/2024", companhia: "Tranquilidade", apolice: "POL-811227", ramo: "Auto", premio: "973,62", comiss: "284,20" },
      { data: "01/10/2024", companhia: "Mapfre", apolice: "POL-430589", ramo: "Auto", premio: "1.969,28", comiss: "522,45" },
      { data: "04/10/2024", companhia: "Mapfre", apolice: "POL-953085", ramo: "Auto", premio: "811,98", comiss: "210,38" },
      { data: "06/10/2024", companhia: "Mapfre", apolice: "POL-430589", ramo: "Auto", premio: "969,64", comiss: "213,81" },
      { data: "06/12/2024", companhia: "Mapfre", apolice: "POL-953085", ramo: "Auto", premio: "280,78", comiss: "79,85" },
    ];

    for (const p of payments) {
      const tr = document.createElement("tr");
      tr.innerHTML = `
        <td>${p.data}</td>
        <td>${p.companhia}</td>
        <td class="code">${p.apolice}</td>
        <td>${p.ramo}</td>
        <td class="num">${p.premio}</td>
        <td class="num">${p.comiss}</td>
      `;
      tbody.appendChild(tr);
    }

    scroller.appendChild(table);
    wrap.appendChild(scroller);
    return wrap;
  };

  const createMetricsAndRecs = () => {
    const wrap = document.createElement("div");
    wrap.className = "md";
    wrap.innerHTML = `
      <hr/>
      <h3>Métricas e Análise</h3>
      <ul>
        <li><strong>Total de Prémios Pagos em 2024:</strong> 17.707,02 €</li>
        <li><strong>Total de Comissões em 2024:</strong> 4.051,05 €</li>
        <li><strong>Número de Apólices em 2024:</strong> 3 (todas do ramo Auto)</li>
        <li><strong>Tendência:</strong> Forte concentração em seguros automóvel, com pagamentos regulares e prémios elevados.</li>
        <li><strong>Estado das Apólices:</strong> Atualmente, todas as apólices estão expiradas ou canceladas — oportunidade para reativação ou novas propostas.</li>
      </ul>
      <h3>Recomendações</h3>
      <ul>
        <li>Sugerir reavaliação das necessidades de seguro automóvel e explorar opções de renovação ou novos produtos.</li>
        <li>Possível abordagem para diversificação de produtos (ex: multirriscos, saúde ou vida).</li>
        <li>Contactar o cliente para atualização do portefólio e identificação de novas oportunidades.</li>
      </ul>
      <p class="dim">Se pretender exportar este relatório para Excel ou analisar outros detalhes (por exemplo, evolução mensal, por companhia, ou por ramo), indique por favor!</p>
    `;
    return wrap;
  };

  // ===== Conteúdos das mensagens (HTML) =====
  const greetUser = "Olá Crafteer";
  const greetBot = wrapMd(`
    Olá! Como posso ajudar hoje com análise de dados ou simulações de seguros?
    Se precisar de relatórios sobre clientes, simulações (auto, vida, saúde ou multirriscos), ou pesquisa em documentos,
    basta indicar o que pretende. Estou pronto para apoiar com insights detalhados e ferramentas avançadas de análise!
  `);

  const qTop10 = "Quero que me indiques os 10 clientes que pagaram mais prémios em 2024";
  const top10Html = wrapMd(`
    Aqui estão os 10 clientes que pagaram mais prémios de seguro em 2024:

    <ol>
      <li>Martim Edgar de Branco (NIF: 642 276 455) — 17.707,02 €</li>
      <li>Emanuel Emanuel Cruz (NIF: 285 374 222) — 12.841,61 €</li>
      <li>Fabiana Helena Carvalho Faria Loureiro (NIF: 771 992 475) — 12.156,11 €</li>
      <li>Joaquim Barbosa Neves (NIF: 671 255 843) — 11.817,57 €</li>
      <li>Henrique Rodrigues Nunes (NIF: 718 223 608) — 11.617,00 €</li>
      <li>Fábio Joaquim de Gaspar (NIF: 724 768 670) — 11.338,15 €</li>
      <li>Leandro Samuel Valente Gaspar Andrade (NIF: 904 375 455) — 11.135,97 €</li>
      <li>Kyara Mafalda Amorim (NIF: 725 625 392) — 10.561,55 €</li>
      <li>Teresa Luna Pinheiro (NIF: 985 388 501) — 10.549,52 €</li>
      <li>Yara Lorena Mendes de Baptista (NIF: 880 233 800) — 9.889,54 €</li>
    </ol>

    <p class="dim">Se pretender exportar esta lista para Excel ou analisar outros detalhes (por exemplo, ramos de seguro ou evolução mensal), indique por favor!</p>
  `);

  const qReport = "faz um relatório histórico do cliente com o NIF 642276455";
  const clientReportTop = wrapMd(`
    Segue o relatório histórico detalhado do cliente Martim Edgar de Branco (NIF: 642 276 455):

    <h3>Dados do Cliente</h3>
    <ul class="kv">
      <li><strong>Nome:</strong> Martim Edgar de Branco</li>
      <li><strong>NIF:</strong> 642 276 455</li>
      <li><strong>Tipo:</strong> Empresa</li>
      <li><strong>Cidade:</strong> Porto</li>
      <li><strong>Código Postal:</strong> 4180</li>
      <li><strong>Telefone:</strong> 967 911 984</li>
      <li><strong>Email:</strong> cblock@example.com</li>
      <li><strong>Consultor:</strong> Carlos Oliveira</li>
      <li><strong>Lead:</strong> Referência</li>
      <li><strong>Data de Nascimento:</strong> 23/11/1969</li>
      <li><strong>Data de Criação:</strong> 18/08/2025</li>
    </ul>

    <hr/>
    <h3>Histórico de Apólices</h3>
    <ol class="pols">
      <li><strong>POL-430589</strong> (Mapfre – Auto)
        <ul>
          <li>Estado: Expirada</li>
          <li>Pagamento: Semestral</li>
          <li>Vigência: 26/11/2023 a 26/11/2024</li>
          <li>Prémio Total Esperado: 2.215,70 €</li>
          <li>Comissão Total Esperada: 430,95 €</li>
        </ul>
      </li>
      <li><strong>POL-811227</strong> (Tranquilidade – Auto)
        <ul>
          <li>Estado: Cancelada</li>
          <li>Pagamento: Anual</li>
          <li>Vigência: 25/12/2023 a 25/12/2024</li>
          <li>Prémio Total Esperado: 2.762,55 €</li>
          <li>Comissão Total Esperada: 599,47 €</li>
        </ul>
      </li>
      <li><strong>POL-953085</strong> (Mapfre – Auto)
        <ul>
          <li>Estado: Cancelada</li>
          <li>Pagamento: Trimestral</li>
          <li>Vigência: 17/02/2024 a 17/02/2025</li>
          <li>Prémio Total Esperado: 401,77 €</li>
          <li>Comissão Total Esperada: 90,24 €</li>
        </ul>
      </li>
    </ol>
  `);

  const qSimul = "faz uma simulação de seguro de vida para 200.000€ para este cliente";
  const simulHtml = wrapMd(`
    A simulação de seguro de vida para Martim Edgar de Branco (NIF: 642 276 455), com capital segurado de <strong>200.000 €</strong>, foi iniciada com sucesso.

    <ul>
      <li><strong>NIF:</strong> 642 276 455</li>
      <li><strong>Data de nascimento:</strong> 23/11/1969</li>
      <li><strong>Capital segurado:</strong> 200.000 €</li>
      <li><strong>Data prevista de início da apólice:</strong> 26/09/2025</li>
    </ul>

    <p class="dim">A simulação está a ser processada e poderá consultar os detalhes completos em breve. Se desejar receber o resultado por email, exportar para Excel ou analisar diferentes capitais/condições, por favor indique!</p>

    <div class="card">
      <div class="head">
        <div class="meta">
          <span>${icon('<path d="M3 12h18M3 6h18M3 18h18"/>')}</span>
          <div><strong>Simulação de seguro</strong><br/><small>Simulation • #52</small></div>
        </div>
        <span class="pill pill--ok">Concluído</span>
        <div class="open" title="Abrir">${icon('<path d="M9 18l6-6-6-6"/>')}</div>
      </div>
      <div class="body">Resumo disponível para consulta.</div>
    </div>
  `);

  const qEmail = "quero então enviar um email com descrição das coberturas e o prémio fracionado mensalmente de 71,84€";
  const emailHtml = wrapMd(`
    O email com a descrição das coberturas do seguro de vida e o prémio mensal de <strong>71,84 €</strong> foi criado e está em processamento. Eis o resumo do que será enviado:

    <hr/>
    <p><strong>Para:</strong> cblock@example.com</p>
    <p><strong>Assunto:</strong> Simulação de Seguro de Vida – Coberturas e Prémio Mensal</p>

    <p><strong>Corpo do email:</strong> Exmo. Sr. Martim Edgar de Branco,</p>
    <p>Segue a descrição das principais coberturas de um seguro de vida:</p>
    <ul>
      <li>Pagamento do capital seguro aos beneficiários designados em caso de morte da Pessoa Segura durante a vigência do contrato.</li>
      <li>Possibilidade de cobertura de invalidez absoluta e definitiva, dependendo do produto contratado.</li>
      <li>O capital seguro pode ser utilizado para proteção familiar, liquidação de créditos (ex: habitação), entre outros fins.</li>
      <li>Os beneficiários podem ser designados livremente, ou, em caso de omissão, serão os herdeiros legais.</li>
    </ul>
    <p>O prémio fracionado mensalmente para o capital de <strong>200.000 €</strong> é de <strong>71,84 €</strong>.</p>
    <p>Caso pretenda mais detalhes sobre exclusões, coberturas adicionais ou condições específicas, estamos ao dispor para esclarecer.</p>
    <p>Com os melhores cumprimentos,<br/>[Crafteer Seguros]</p>

    <div class="status ok">
      <span class="dot"></span>
      O worker de email foi criado com sucesso (ID do Worker: 53) e está a ser processado. Assim que o email estiver pronto, poderá ser revisto e enviado.
    </div>

    <p class="dim">Se desejar acrescentar ou alterar algum detalhe antes do envio, por favor indique!</p>

    <div class="card">
      <div class="head">
        <div class="meta">
          <span>${icon('<path d="M4 4h16v14H4z"/><path d="M22 6l-10 7L2 6"/>')}</span>
          <div><strong>Simulação de Seguro de Vida – Coberturas e Prémio Mensal</strong><br/><small>Email • #53</small></div>
        </div>
        <span class="pill pill--warn">Pronto para revisão</span>
        <div class="open" title="Abrir" data-action="open-email">${icon('<path d="M9 18l6-6-6-6"/>')}</div>
      </div>
      <div class="body">Mensagem preparada e registada no sistema.</div>
    </div>
  `);

  const qCulpa = "Este cliente circulava atrás de uma viatura e embateu por trás. Quem tem culpa?";
  const culpaHtml = wrapMd(`
    No contexto dos seguros automóvel em Portugal, e de acordo com as cláusulas das Condições Gerais de várias seguradoras (exemplo: Fidelidade Auto), a responsabilidade em acidentes de embate por trás (colisão traseira) é tipicamente atribuída ao veículo que circulava atrás.

    Isto deve-se ao facto de o Código da Estrada e as condições das apólices considerarem que o condutor que circula atrás deve manter sempre uma distância de segurança suficiente para evitar a colisão, mesmo que o veículo da frente abrande ou pare subitamente.

    <h3>Resumo prático:</h3>
    <ul>
      <li>Se um cliente embate por trás noutro veículo, a culpa é, por regra, do condutor que circulava atrás.</li>
      <li>O seguro de responsabilidade civil do veículo que embateu por trás será chamado a assumir os danos causados ao veículo da frente.</li>
    </ul>

    <h3>Fundamento legal e contratual:</h3>
    <ul>
      <li>As condições gerais do seguro automóvel definem “colisão” como o embate de um veículo em movimento contra outro corpo em movimento.</li>
      <li>O contrato cobre a responsabilidade civil do condutor responsável pelo acidente, incluindo danos materiais causados a terceiros, salvo exclusões específicas.</li>
    </ul>

    <p class="dim">Se precisar de consultar a cláusula exata ou obter um parecer detalhado para apresentação ao cliente ou à seguradora, posso facultar o excerto do documento relevante. Se desejar, posso também enviar esta explicação por email ao cliente ou ao consultor.</p>
  `);

  const qAlcool = "este cliente teve um acidente com culpa mas encontrava-se sob o efeito de álcool quando embateu. Achas que a seguradora vai pagar ao terceiro lesado?";
  const alcoolHtml = wrapMd(`
    De acordo com as Condições Gerais do Seguro Automóvel (exemplo: Fidelidade Auto), a situação de acidente com culpa em que o condutor se encontra sob o efeito de álcool está expressamente prevista nas cláusulas de exclusões e direito de regresso:

    <ul>
      <li><strong>Exclusão de Cobertura ao Condutor:</strong> Os danos sofridos pelo próprio condutor que conduz com uma taxa de alcoolemia superior à legalmente admitida <u>não</u> estão cobertos pelo seguro. Isto significa que o seguro não paga os danos pessoais do condutor nestas circunstâncias.</li>
      <li><strong>Responsabilidade Civil Obrigatória (terceiros):</strong> Mesmo que o condutor esteja alcoolizado, a seguradora é obrigada por lei a indemnizar o terceiro lesado ao abrigo do seguro obrigatório de responsabilidade civil automóvel.</li>
      <li><strong>Direito de Regresso:</strong> Após indemnizar o terceiro lesado, a seguradora pode exercer o direito de regresso contra o seu próprio segurado (ou condutor responsável), exigindo-lhe o reembolso do valor pago ao terceiro.</li>
    </ul>

    <h3>Resumo Prático:</h3>
    <ul>
      <li>A seguradora vai pagar ao terceiro lesado, cumprindo a lei.</li>
      <li>Depois, pode exigir ao cliente (condutor com álcool) o reembolso desse valor.</li>
    </ul>

    <h3>Cláusula relevante (exemplo Fidelidade Auto):</h3>
    <blockquote>
      “Ficam sempre excluídos os danos resultantes de acidente ocorrido quando o condutor do veículo seguro conduza com uma taxa de alcoolemia superior à legalmente admitida (...). A seguradora, tendo pago ao terceiro lesado, tem direito de regresso contra o tomador do seguro ou condutor responsável.”
    </blockquote>
  `);

  // email curto em tom de “tu”
  const qEmailTu = "Quero enviar então um email ao cliente com explicação desta situação. Email curto e conciso. Quero tratar o cliente por tu, de forma próxima";
  const emailTuHtml = wrapMd(`
    O email foi criado e está em processamento. Eis o que será enviado ao cliente:

    <hr/>
    <p><strong>Para:</strong> cblock@example.com</p>
    <p><strong>Assunto:</strong> Acidente com álcool — responsabilidade do seguro</p>

    <p><strong>Corpo do email:</strong></p>
    <p>Olá Martim,</p>
    <p>Só para te explicar de forma simples: mesmo que tenhas tido um acidente com álcool, o seguro é obrigado a pagar os danos ao outro carro/pessoa. No entanto, depois pode pedir-te a ti o reembolso desse valor. Ou seja, o terceiro fica protegido, mas podes ter de devolver à seguradora o que ela pagou.</p>
    <p>Se quiseres mais detalhes ou exemplos, diz!</p>
    <p>Abraço,<br/>Crafteer Seguros</p>

    <div class="status ok">
      <span class="dot"></span>
      O worker de email foi criado com sucesso (ID do Worker: 54) e está a ser processado. Assim que o email estiver pronto, poderás revê-lo e enviá-lo.
    </div>

    <div class="card">
      <div class="head">
        <div class="meta">
          <span>${icon('<rect x="3" y="4" width="18" height="14" rx="2"/><path d="M22 6l-10 7L2 6"/>')}</span>
          <div><strong>Acidente com álcool — responsabilidade do seguro</strong><br/><small>Email • #54</small></div>
        </div>
        <span class="pill pill--warn">Pronto para revisão</span>
        <div class="open" title="Abrir" data-action="open-email">${icon('<path d="M9 18l6-6-6-6"/>')}</div>
      </div>
      <div class="body">Rascunho criado. Clica para rever antes de enviar.</div>
    </div>
  `);

  // ===== Execução da conversa =====
  async function showUser(text: string) {
    if (isAborted()) return;

    const composerNow = composerRef.current;
    await moveCursorTo(composerNow, 650);

    const composer = composerRef.current;
    if (!composer || isAborted()) return;
    composer.value = "";
    composer.dispatchEvent(new Event("input"));
    for (const ch of text) {
      if (isAborted()) return;
      composer.value += ch;
      composer.dispatchEvent(new Event("input"));
      scrollBottom();
      await wait(18); // velocidade “humana” do utilizador
    }

    await moveCursorTo(sendBtnRef.current, 520);
    await clickEl(sendBtnRef.current);

    const { bubble } = makeMsg("user");
    const div = document.createElement("div");
    div.style.whiteSpace = "pre-wrap";
    bubble.appendChild(div);
    div.textContent = text;
  }

  async function run() {
    if (isRunning) return;
    setIsRunning(true);
    if (isAborted()) return;

    if (chatRef.current) chatRef.current.innerHTML = "";
    if (cursorRef.current) cursorRef.current.style.transform = "translate(40px,40px)";

    // 16:05 — greeting
    await showUser(greetUser);
    addTime("16:05");
    await showBotStreamHtml(greetBot);
    addTime("16:06");

    // 16:06 — top10
    await showUser(qTop10);
    await showBotStreamHtml(top10Html);
    addTime("16:07");

    // 16:07 — relatório + tabela + métricas
    await showUser(qReport);
    const table = createPaymentsTable();
    const extras = createMetricsAndRecs();
    const reportAll = `${clientReportTop}${table.outerHTML}${extras.outerHTML}`;
    await showBotStreamHtml(reportAll);
    addTime("16:08");

    // 16:08 — simulação 200k
    await showUser(qSimul);
    await showBotStreamHtml(simulHtml);
    addTime("16:09");

    // 16:09 — email com 71,84 €
    await showUser(qEmail);
    await showBotStreamHtml(emailHtml);
    addTime("16:10");

    // 16:11 — culpa embate atrás
    await showUser(qCulpa);
    await showBotStreamHtml(culpaHtml);
    addTime("16:12");

    // 16:12 — acidente com álcool
    await showUser(qAlcool);
    await showBotStreamHtml(alcoolHtml);
    addTime("16:13");

    // 16:14 — email curto e próximo (“tu”)
    await showUser(qEmailTu);
    await showBotStreamHtml(emailTuHtml);

    // abrir o cartão e enviar o email (animação)
    await animateOpenAndSendEmail();
    addTime("16:14");

    setIsRunning(false);
    completeOnce();
  }

  // contador do composer
  useEffect(() => {
    const handle = () => {
      if (countRef.current && composerRef.current)
        countRef.current.textContent = `${composerRef.current.value.length}/4000`;
    };
    composerRef.current?.addEventListener("input", handle);
    return () => composerRef.current?.removeEventListener("input", handle);
  }, [token]);

  // Intersection Observer para iniciar animação quando aparece na viewport
  useEffect(() => {
    const chatElement = chatRef.current?.closest('.chat-container');
    if (!chatElement) return;

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !isRunning) {
          // Iniciar animação quando entra na viewport
          makeAbort();
          completedRef.current = false;
          setIsRunning(false);
          const hard = setTimeout(() => completeOnce(), 120000);
          const t = setTimeout(run, 400);

          // Cleanup function para o timeout
          const cleanup = () => {
            clearTimeout(t);
            clearTimeout(hard);
          };

          // Armazenar cleanup para poder cancelar se necessário
          (entry.target as any).__cleanup = cleanup;
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.3,
      rootMargin: '0px 0px -100px 0px'
    });

    observer.observe(chatElement);

    return () => {
      observer.disconnect();
      // Cleanup dos timeouts se existirem
      const cleanup = (chatElement as any).__cleanup;
      if (cleanup) cleanup();
    };
  }, [token, loop, isRunning]);

  // (re)inicia ao mudar token/loop - apenas se já estiver visível
  useEffect(() => {
    // Só reinicia se o componente já estiver visível na viewport
    const chatElement = chatRef.current?.closest('.chat-container');
    if (!chatElement) return;

    const rect = chatElement.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

    if (isVisible) {
      makeAbort();
      completedRef.current = false;
      setIsRunning(false);
      const hard = setTimeout(() => completeOnce(), 120000);
      const t = setTimeout(run, 400);
      return () => { clearTimeout(t); clearTimeout(hard); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, loop]);

  // abort no unmount
  useEffect(() => () => abortRef.current?.abort(), []);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = composerRef.current;
    if (textarea) {
      const autoResize = () => {
        textarea.style.height = 'auto';
        const scrollHeight = textarea.scrollHeight;
        const minHeight = 80; // Min height from CSS
        const maxHeight = 150; // Max height from CSS
        const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
        textarea.style.height = newHeight + 'px';
      };

      // Initial resize
      autoResize();

      // Add event listeners for content changes
      textarea.addEventListener('input', autoResize);
      textarea.addEventListener('paste', autoResize);

      // Resize on window resize
      window.addEventListener('resize', autoResize);

    return () => {
        textarea.removeEventListener('input', autoResize);
        textarea.removeEventListener('paste', autoResize);
        window.removeEventListener('resize', autoResize);
      };
    }
  }, []);

  return (
    <div
      className="chat-animation non-interactive"
      onMouseEnter={() => {
        // console.log('Pausando animação (ChatAnimation)');
        setIsPaused(true);
      }}
      onMouseLeave={() => {
        // console.log('Retomando animação (ChatAnimation)');
        setIsPaused(false);
      }}
      onTouchStart={() => {
        // console.log('Touch detectado - pausando animação (ChatAnimation)');
        setIsPaused(true);
      }}
      onTouchEnd={() => {
        // console.log('Touch terminado - retomando animação (ChatAnimation)');
        // Delay para permitir scroll
        setTimeout(() => {
          setIsPaused(false);
        }, 1000);
      }}
    >
        <section className="chatApp" style={{ padding: "0px" }}>
          <div className="topbar">
            <div className="crumb"><span className="dim">Dashboard</span><span className="sep">›</span><strong>Chat</strong></div>
            <div className="head-rt">
              <div className="search">
                <span className="ico">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
                  </svg>
                </span>
                <input placeholder="Pesquise" tabIndex={-1} readOnly aria-hidden="true"/>
              </div>
            </div>
          </div>

          <div className="chat" ref={chatRef} />

          <div className="composer">
            <div className="box">

              <textarea
                ref={composerRef}
                tabIndex={-1}
                readOnly
                placeholder="Escreva a sua mensagem… (Shift+Enter para nova linha)"
                aria-hidden="true"
              />

              <div className="addons" aria-hidden="true">
                <button className="ico-btn" title="Anexar" tabIndex={-1}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M21.44 11.05l-8.49 8.49a6 6 0 1 1-8.49-8.49l8.49-8.49a4 4 0 1 1 5.66 5.66l-8.49 8.49a2 2 0 1 1-2.83-2.83L15 6"/>
                  </svg>
                </button>
                <button className="ico-btn" title="Microfone" tabIndex={-1}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <rect x="9" y="4" width="6" height="10" rx="3"/><path d="M5 10v2a7 7 0 0 0 14 0v-2"/><path d="M12 19v3"/>
                  </svg>
                </button>
                <button className="send" ref={sendBtnRef} aria-label="Enviar" tabIndex={-1}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 19V5M5 12l7-7 7 7"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="hint"><span>Prima Enter para enviar • Shift+Enter para nova linha</span><span ref={countRef}>0/4000</span></div>
          </div>
        </section>
    </div>
  );
};

export default ChatAnimation;
